import { getPlayer } from "@/api/privateRequests/players/getPlayer";
import Toolbar from "@/containers/Toolbar";
import type { CompareOrigin } from "@/events/compareMixpanel";
import * as evolutionsMixpanel from "@/events/evolutionsMixpanel";
import type { EvolutionsSubsection } from "@/events/evolutionsMixpanel";
import { mixpanelTrack } from "@/events/mixpanelTrack";
import * as playersMixpanel from "@/events/playersMixpanel";
import type { PlayersSubsection } from "@/events/playersMixpanel";
import * as squadBuilderMixpanel from "@/events/squadBuilderMixpanel";
import { useSticky } from "@/hooks/useSticky";
import { cn } from "@/lib/utils";
import { EvolutionChainCard } from "@/pages/EvolutionsHub/sections/EvolutionChainCard";
import { addPlayerToEvolutionBuilder } from "@/redux/evolutions/evolutionsBuilderSlice";
import { includesAtLeastOneExpiredEvolutionSelector } from "@/redux/evolutions/evolutionsSlice";
import { topToPageSelector } from "@/redux/globalState/globalStateSlice";
import { getVersionAssetsSelector } from "@/redux/loadedContent/loadedContentSlice";
import { addAlert, showModal } from "@/redux/message/messageSlice";
import {
  generateNavigationUrl,
  versionNameBySlug,
  versionUrlSlug,
} from "@/redux/players/playersSlice";
import { AppDispatch } from "@/redux/store";
import {
  canUseClubImportSelector,
  userIsLoggedInSelector,
} from "@/redux/user/userSlice";
import { BREAKPOINTS } from "@/settings";
import type { BackendPlayerType } from "@repo/shared/api";
import {
  evolutionPathFromParam,
  evolutionPathParam,
} from "@repo/shared/evolutions";
import { useTranslation } from "@repo/shared/i18n";
import { saveEvolution } from "@repo/shared/redux";
import {
  CardFont,
  Cosmetic,
  EvoPathId,
  Foil,
  MetaRating,
  ObjectiveDisplay,
  PriceInfo,
} from "@repo/shared/types";
import { capitalizeWordsAndRemoveDashes } from "@repo/shared/utils";
import { useEffect, useMemo, useReducer, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  useLocation,
  useNavigate,
  useOutletContext,
  useParams,
  useSearchParams,
} from "react-router";
import { useMediaQuery } from "usehooks-ts";

import { PlayerNotFound, isNotFoundError } from "./PlayerNotFound";
import {
  Chemistry,
  DetailedPlayerAction,
  detailedPlayerReducer,
} from "./reducer";
import {
  AlternativeVersion,
  AlternativeVersionType,
} from "./sections/AlternativeVersionsSection";
import { AttributesSection } from "./sections/Attributes/AttributesSection";
import PlayerMainStatsSection from "./sections/MainStatsSection";
import { DetailedPlayStylesSection } from "./sections/PlayStyles/DetailedPlayStylesSection";
import { PlayStylesSection } from "./sections/PlayStyles/PlayStylesSection";
import { PlayerBio } from "./sections/PlayerBioSection";
import { RolesSection } from "./sections/RolesSection";
import {
  DetailedPlayerToolbar,
  TopSection,
} from "./sections/TopSection/TopSection";
import { AllRolesSection } from "./sections/const AllRolesSection";

type inGameAttributes = {
  [key: string]: number;
};

export type DetailedPlayerContext = {
  assetId: number;
  attributes: number[];
  resourceId: number;
  cardName: string;
  cardFont?: CardFont;
  foil?: Foil;
  cosmetics?: Cosmetic[];
  name: string;
  countryId: number;
  leagueId: number;
  clubId: number;
  canEvolve: boolean;
  rating: number;
  metalId: number;
  versionId: number;
  hasDynamicImage: boolean;
  preferredPosition: string;
  possiblePositions: string[];
  playStylesPlus: number[];
  playStyles: number[];
  playerUrl: string;
  skillMoves: 1 | 2 | 3 | 4 | 5;
  weakFoot: 1 | 2 | 3 | 4 | 5;
  preferredFoot: "left" | "right";
  metaRating: number;
  newPlayer: boolean;
  owned: boolean;
  firstOwner?: boolean;
  rolesPlus: number[];
  priceInfo?: PriceInfo;
  objectiveDisplay?: ObjectiveDisplay;
  playerRoleId: number;
  evoPathId?: EvoPathId;
  evolutionPath?: number[];
  pathwayPath?: number[][];
  isObjective?: boolean;
  isSbc?: boolean;
  itemScore?: number;
};

interface DetailedPlayerLocationState {
  chemistryContext: Chemistry;
  chemStyleContext: number;
  p: DetailedPlayerContext;
}

export type DetailedPlayerType = DetailedPlayerContext & {
  bodyTypeId: number;
  cardType: string;
  height: number;
  weight: number;
  metaRatings: MetaRating[];
  inGameAttributes: inGameAttributes;
  alternativeVersions: AlternativeVersionType[];
  gender: "MALE" | "FEMALE";
};

export const GridBox = ({
  children,
  title,
  className,
  noLayout,
}: {
  children: React.ReactNode;
  title: string;
  className?: string;
  noLayout?: boolean;
}) => (
  <article
    className={cn(
      className,
      !noLayout && "bg-panel-background rounded-xl pt-2",
    )}
  >
    {!noLayout && (
      <h3 className="text-xs mobile:text-sm font-semibold text-dark-tertiary px-4 mobile:pt-1 mobile:mb-1.5">
        {title}
      </h3>
    )}
    {children}
  </article>
);

// Remount per player: the reducer's role, chem and meta ratings would otherwise
// carry over when navigating straight from one player page to another.
export const DetailedPlayer = () => {
  const { pathname } = useLocation();
  return <DetailedPlayerPage key={pathname} />;
};

const DetailedPlayerPage = () => {
  const [player, setPlayer] = useState<DetailedPlayerType>();
  const [fetchingPlayer, setFetchingPlayer] = useState(false);
  const [playerNotFound, setPlayerNotFound] = useState(false);
  const { resourceId: urlResourceId, name, version } = useParams();
  const outletContextPlayer = useOutletContext<
    DetailedPlayerType | undefined
  >();
  const location = useLocation();
  const canUseClubImport = useSelector(canUseClubImportSelector);

  const [searchParams] = useSearchParams();
  const [showAllRoles, setShowAllRoles] = useState(false);
  const isMobile = useMediaQuery(BREAKPOINTS.MOBILE.query);
  const isTouchDevice = useMediaQuery(BREAKPOINTS.TOUCH_DEVICE.query);
  const [chemStylesOpen, setChemStylesOpen] = useState(
    !localStorage.getItem("chemStylesOpen") ||
      localStorage.getItem("chemStylesOpen") === "true",
  );
  const isLoggedIn = useSelector(userIsLoggedInSelector);
  const [isAlternativeVersionsOpenMobile, setIsAlternativeVersionsOpenMobile] =
    useState(false);
  const { p: playerContext, chemistryContext = 0 } =
    (location.state as DetailedPlayerLocationState | null) ?? {};
  const topToPage = useSelector(topToPageSelector);
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation("players");
  const navigate = useNavigate();
  const [scrollPastTopBar, refTopBar] = useSticky({
    triggerOn: "bottom",
    offset: 48,
  }) as [boolean, React.MutableRefObject<HTMLDivElement>];
  const versionAssets = useSelector(getVersionAssetsSelector);
  const playerName = player?.name || capitalizeWordsAndRemoveDashes(name);
  const versionName =
    versionNameBySlug(versionAssets, version) ??
    versionAssets[player?.versionId]?.name ??
    capitalizeWordsAndRemoveDashes(version);
  const [state, useReducerDispatch] = useReducer(detailedPlayerReducer, {
    chemistry: {
      currentChemistry: 0,
      temporaryChemistry: undefined,
    },
    chemStyle: {
      currentChemstyleId: undefined,
      temporaryChemstyleId: undefined,
      bestChemStyleId: undefined,
      hasSelectedChemStyleManually: false,
    },
    playerRoleId:
      +searchParams.get("player-role-id") ||
      playerContext?.playerRoleId ||
      undefined,
    metaRating: {
      currentMetaRating: playerContext?.metaRating,
      temporaryMetaRating: undefined,
      metaRatingZeroChem: undefined,
    },
    metaRatings: [],
  });
  const [awaitingLogin, setAwaitingLogin] = useState<"save" | undefined>();
  const [saving, setSaving] = useState(false);

  const labPlayStylesPlus = searchParams.get("lab-playstyles-plus");
  const labPlayStyles = searchParams.get("lab-playstyles");

  const isFromEvoBuilder = useMemo(() => {
    return location.pathname.includes("/builder/");
  }, [location.pathname]);

  const isFromSaved = useMemo(() => {
    return location.pathname.includes("/saved/");
  }, [location.pathname]);

  const isFromPlaystylesLab = useMemo(() => {
    return location.pathname.includes("/playstyles-lab/");
  }, [location.pathname]);

  // Mixpanel section/subsection for this player-detail page, derived from the
  // route it is rendered under (see Routes.tsx). Used for the share and
  // added_player_to_evolution_builder events fired from the top toolbar.
  // The PlayStyles Lab and the Evo Builder both count as "Builder" (building an
  // evolution player); /evolutions/* otherwise is the Player View.
  const playerViewTrackingContext = useMemo(():
    | { section: "evolutions"; subsection: EvolutionsSubsection }
    | { section: "players"; subsection: PlayersSubsection }
    | { section: "squad-builder" } => {
    const p = location.pathname;
    if (p.includes("/squad-builder/")) return { section: "squad-builder" };
    if (p.includes("/playstyles-lab/") || p.includes("/builder/"))
      return { section: "evolutions", subsection: "Builder" };
    if (p.includes("/saved/"))
      return { section: "evolutions", subsection: "Planned Evolutions" };
    if (p.includes("/evolutions/"))
      return { section: "evolutions", subsection: "Player View" };
    return { section: "players", subsection: "Player View" };
  }, [location.pathname]);

  // Same route context in the shape the compare events want (display-cased
  // section names, and Squad Builder's single "Normal" subsection spelled out).
  const compareOrigin = useMemo((): CompareOrigin => {
    if (playerViewTrackingContext.section === "evolutions")
      return {
        section: "Evolutions",
        subsection: playerViewTrackingContext.subsection,
      };
    if (playerViewTrackingContext.section === "players")
      return {
        section: "Players",
        subsection: playerViewTrackingContext.subsection,
      };
    return { section: "Squad Builder", subsection: "Normal" };
  }, [playerViewTrackingContext]);

  const resourceId = useMemo(() => {
    if (urlResourceId) {
      return parseInt(urlResourceId);
    }
    if (isFromEvoBuilder && outletContextPlayer?.resourceId) {
      return outletContextPlayer.resourceId;
    }
    if (playerContext?.resourceId) {
      return playerContext.resourceId;
    }
    return undefined;
  }, [urlResourceId, isFromEvoBuilder, outletContextPlayer, playerContext]);

  const evoPathId = useMemo(() => {
    if (searchParams.get("evo-path-id")) {
      return searchParams.get("evo-path-id");
    }
    if (playerContext?.evoPathId) {
      return playerContext.evoPathId;
    }
    return undefined;
  }, [playerContext?.evoPathId, searchParams]);

  const urlEvolutionPath = useMemo(
    () => evolutionPathFromParam(searchParams.get("evolution-path")),
    [searchParams],
  );
  const evolutionPath = useMemo(
    () => urlEvolutionPath?.evolutionPath ?? playerContext?.evolutionPath,
    [urlEvolutionPath, playerContext?.evolutionPath],
  );
  const pathwayPath = useMemo(
    () => urlEvolutionPath?.pathwayPath ?? playerContext?.pathwayPath,
    [urlEvolutionPath, playerContext?.pathwayPath],
  );

  const isExpired = useSelector(
    includesAtLeastOneExpiredEvolutionSelector({
      ids: evolutionPath ?? [],
    }),
  );

  // Use player from Outlet context if coming from evolution builder
  useEffect(() => {
    if (isFromEvoBuilder && outletContextPlayer) {
      setPlayer(outletContextPlayer as DetailedPlayerType);
      // Set meta ratings if available

      if (
        outletContextPlayer.metaRatings &&
        Array.isArray(outletContextPlayer.metaRatings)
      ) {
        handleAction({
          type: "SET_META_RATINGS",
          payload: outletContextPlayer.metaRatings,
        });
      }
    }

    // Otherwise, use playerContext from location.state if available
    if (playerContext) {
      setPlayer({
        ...player,
        ...playerContext,
        resourceId: resourceId,
      });
    }
    if (chemistryContext) {
      handleAction({
        type: "SET_CHEMISTRY",
        payload: chemistryContext,
      });
    }
  }, [
    isFromEvoBuilder,
    outletContextPlayer,
    playerContext,
    resourceId,
    chemistryContext,
  ]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Only fetch player if NOT coming from evolution builder
  useEffect(() => {
    if (
      isFromEvoBuilder &&
      (player?.metaRatings || outletContextPlayer?.metaRatings)
    ) {
      handleAction({
        type: "SET_META_RATINGS",
        payload: player?.metaRatings ?? outletContextPlayer?.metaRatings ?? [],
      });
      // Skip fetch - use outlet context player
      return;
    }

    // Don't fetch if resourceId is not available
    if (!resourceId) {
      return;
    }

    // Prevent duplicate fetches
    if (fetchingPlayer) {
      return;
    }

    const fetchPlayer = async () => {
      try {
        setFetchingPlayer(true);
        let backendPlayerType: BackendPlayerType;
        if (isFromEvoBuilder) {
          backendPlayerType = "evo-builder";
        } else if (isFromPlaystylesLab) {
          backendPlayerType = "lab-player";
        } else if (isFromSaved) {
          backendPlayerType = "planned-player";
        } else if (isExpired) {
          backendPlayerType = "expired";
        } else if (evolutionPath?.length > 0) {
          backendPlayerType = "evo-finder";
        } else {
          backendPlayerType = "base";
        }

        const player = await getPlayer({
          resourceId: resourceId,
          playerRoleId: state.playerRoleId,
          evoPathId: evoPathId,
          evolutionPath: evolutionPath,
          pathwayPath,
          backendPlayerType,
          labPlayStylesPlus: labPlayStylesPlus?.split(",").map(Number),
          labPlayStyles: labPlayStyles?.split(",").map(Number),
        });

        // Default to the backend's best role when no role is specified.
        if (!state.playerRoleId && player.playerRoleId) {
          handleAction({
            type: "SET_PLAYER_ROLE_ID",
            payload: player.playerRoleId,
          });
        }

        handleAction({
          type: "SET_META_RATINGS",
          payload: player.metaRatings,
        });
        setPlayer(player);
      } catch (error) {
        console.error(error);
        if (isNotFoundError(error)) setPlayerNotFound(true);
      } finally {
        setFetchingPlayer(false);
      }
    };

    fetchPlayer();
  }, [
    isFromEvoBuilder,
    outletContextPlayer,
    playerContext,
    resourceId,
    evoPathId,
    evolutionPath,
    pathwayPath,
  ]);

  const handleAction = (action: DetailedPlayerAction) => {
    useReducerDispatch(action);
  };

  const handleChemStylesOpen = () => {
    localStorage.setItem("chemStylesOpen", !chemStylesOpen ? "true" : "false");
    setChemStylesOpen(!chemStylesOpen);
  };

  const handleClickedEvolved = () => {
    if (!player || !resourceId) return;

    if (playerViewTrackingContext.section === "evolutions") {
      evolutionsMixpanel.trackAddedPlayerToEvolutionBuilder({
        subsection: playerViewTrackingContext.subsection,
      });
    } else if (playerViewTrackingContext.section === "players") {
      playersMixpanel.trackAddedPlayerToEvolutionBuilder({
        subsection: playerViewTrackingContext.subsection,
      });
    } else {
      squadBuilderMixpanel.trackAddedPlayerToEvolutionBuilder();
    }

    dispatch(
      addPlayerToEvolutionBuilder({
        player: {
          ...player,
          resourceId: resourceId,
          playerRoleId: state.playerRoleId,
          evoPathId: evoPathId,
          evolutionPath: evolutionPath,
          pathwayPath,
        },
        evolutionChain: evolutionPath,
        pathwayChain: pathwayPath,
      }),
    );

    const navLink = generateNavigationUrl({
      name: player.name,
      resourceId: resourceId,
      playerRoleId: state.playerRoleId,
      evoPathId: evoPathId,
      evolutionPath: evolutionPath,
      pathwayPath,
      versionSlug: version || versionUrlSlug(versionAssets[player?.versionId]),
      basePath: "/evolutions/builder",
    });
    navigate(navLink);
  };

  const handleClickedDownloadAssets = () => {
    // share — section/subsection from the route (see playerViewTrackingContext).
    // Squad Builder share is tracked server-side, so it's skipped here.
    if (playerViewTrackingContext.section === "evolutions") {
      evolutionsMixpanel.trackShare({
        subsection: playerViewTrackingContext.subsection,
      });
    } else if (playerViewTrackingContext.section === "players") {
      playersMixpanel.trackShare({
        subsection: playerViewTrackingContext.subsection,
      });
    }

    const versionSlug =
      version || versionUrlSlug(versionAssets[player?.versionId]);
    let queryParams = new URLSearchParams();
    if (evolutionPath?.length > 0) {
      queryParams.set(
        "evolution-path",
        evolutionPathParam(evolutionPath, pathwayPath),
      );
    }
    if (evoPathId) {
      queryParams.set("evo-path-id", evoPathId?.toString());
    }
    if (isFromEvoBuilder || isFromSaved) {
      queryParams.set("is-evo-builder", "true");
    }
    const queryString = queryParams.toString();
    navigate(
      `/players/${name}/${versionSlug}/${resourceId}/images${queryString ? `?${queryString}` : ""}`,
    );
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await dispatch(
        saveEvolution({
          resourceId: resourceId,
          evolutionPath: evolutionPath,
          pathwayPath,
          evoPathId: evoPathId,
        }),
      ).unwrap();
      dispatch(
        addAlert({
          type: "success",
          description: t("detailed.alerts.saved"),
          persistent: true,
          link: {
            label: t("detailed.alerts.viewSaved"),
            to: "/evolutions/my-evolutions/saved",
          },
        }),
      );
      // save (evo_player) is fired server-side in peareasy-services
      // (SavedEvolutionPlayersController) when this POST hits the backend.
    } catch {
      dispatch(
        addAlert({
          type: "error",
          description: t("detailed.alerts.saveFailed"),
        }),
      );
    } finally {
      setSaving(false);
    }
  };

  // Logged out parks the save until sign-in completes.
  const handleClickedSaveEvolution = () => {
    if (!isLoggedIn) {
      dispatch(
        showModal({
          modalType: "login-required",
          freeFeatureLoggedInRequiredHook: "save-evo",
        }),
      );
      setAwaitingLogin("save");
      return;
    }
    handleSave();
  };

  useEffect(() => {
    if (awaitingLogin && isLoggedIn) {
      handleSave();
      setAwaitingLogin(undefined);
    }
  }, [isLoggedIn, awaitingLogin]);

  if (playerNotFound) return <PlayerNotFound />;

  return (
    <main className="bg-panel-base min-h-screen pb-10">
      {isMobile && <div ref={refTopBar} className="absolute top-62" />}
      <TopSection
        name={playerName}
        versionName={versionName}
        player={player}
        metaRating={
          state.metaRating.temporaryMetaRating ??
          state.metaRating.currentMetaRating
        }
        metaRatingZeroChem={state.metaRating.metaRatingZeroChem}
        chemistry={state.chemistry.currentChemistry}
        chemStyleId={state.chemStyle.currentChemstyleId}
        playerRoleId={state.playerRoleId}
        rolesPlus={player?.rolesPlus}
        isEvolution={evolutionPath?.length > 0 || evoPathId !== undefined}
        scrollPastTopBar={scrollPastTopBar}
      />
      {!isMobile && <div ref={refTopBar} className="absolute top-50" />}
      <div
        className={cn(
          scrollPastTopBar && "sticky z-20",
          scrollPastTopBar && !isTouchDevice
            ? topToPage.tailwindString
            : topToPage.closedTopBar
              ? "top-11"
              : "top-20",
          !isMobile && "shadow-soft-dark-bottom",
        )}
      >
        <Toolbar
          color={scrollPastTopBar && isMobile ? "bg-panel-base" : "bg-toolbar"}
          children={
            <DetailedPlayerToolbar
              player={player}
              playerRoleId={state.playerRoleId}
              chemStyleId={state.chemStyle.currentChemstyleId}
              metaRating={
                state.metaRating.temporaryMetaRating ??
                state.metaRating.currentMetaRating
              }
              chemistry={state.chemistry.currentChemistry}
              metaRatingZeroChem={state.metaRating.metaRatingZeroChem}
              playerName={playerName}
              resourceId={resourceId}
              rolesPlus={player?.rolesPlus}
              compareOrigin={compareOrigin}
              scrollPastTopBar={scrollPastTopBar}
              setIsAlternativeVersionsOpenMobile={
                setIsAlternativeVersionsOpenMobile
              }
              isAlternativeVersionsOpenMobile={isAlternativeVersionsOpenMobile}
              handleClickedEvolved={handleClickedEvolved}
              handleClickedDownloadAssets={handleClickedDownloadAssets}
              handleClickedSaveEvolution={handleClickedSaveEvolution}
              savingEvolution={saving}
              isEvolutionPlayer={evolutionPath?.length > 0}
              isFromEvolutionBuilder={isFromEvoBuilder || isFromPlaystylesLab}
              evolutionPath={evolutionPath}
              pathwayPath={pathwayPath}
            />
          }
        />
      </div>
      {isAlternativeVersionsOpenMobile && (
        <div className="flex flex-row gap-x-2 px-4 py-2 bg-panel-middleground">
          {player?.alternativeVersions
            ?.sort((a, b) => a.rating - b.rating)
            .map((version) => (
              <AlternativeVersion
                key={version.resourceId}
                version={version}
                playerName={playerName}
                isActive={version.resourceId === resourceId}
              />
            ))}
        </div>
      )}
      <div className="grid grid-cols-4 max-w-content mx-auto w-full gap-5 mobile:gap-4 px-4 touch-device:px-10 large-desktop:px-20 pt-3 mobile:pt-6">
        {evolutionPath?.length > 0 && (
          <div className="col-span-4 mobile:pb-2">
            <EvolutionChainCard
              evolutionIds={evolutionPath}
              pathwayPath={pathwayPath}
              detailsAction
            />
          </div>
        )}
        <div className="mobile:pb-2 col-span-4 grid grid-cols-4 gap-0.5">
          <PlayerMainStatsSection
            positions={player?.possiblePositions}
            weakFoot={player?.weakFoot}
            skillMoves={player?.skillMoves}
            preferredFoot={player?.preferredFoot}
            bodyTypeId={player?.bodyTypeId}
          />
          <PlayStylesSection
            playStylesPlus={player?.playStylesPlus}
            playStyles={player?.playStyles}
          />
        </div>
        {showAllRoles ? (
          <AllRolesSection
            handleShowAllRoles={() => {
              setShowAllRoles(false);
            }}
            chemistry={state.chemistry.currentChemistry}
            currentPlayerRoleId={state.playerRoleId}
            metaRatingsForAllRoles={state.metaRatings.filter(
              (mr) => mr.isBestChemstyleAtChem,
            )}
            rolesPlus={player?.rolesPlus}
            possiblePositions={player?.possiblePositions}
            handleAction={handleAction}
          />
        ) : (
          <>
            <RolesSection
              rolesPlus={player?.rolesPlus}
              metaRatingsAllRoles={state.metaRatings.filter(
                (mr) => mr.isBestChemstyleAtChem,
              )}
              currentChemistry={state.chemistry.currentChemistry}
              currentPlayerRoleId={state.playerRoleId}
              handleAction={handleAction}
              handleShowAllRoles={() => {
                setShowAllRoles(true);
              }}
              positions={player?.possiblePositions}
              chemStylesOpen={chemStylesOpen}
            />
            <AttributesSection
              inGameAttributes={player?.inGameAttributes}
              overallAttributes={player?.attributes}
              isGk={player?.preferredPosition === "GK"}
              chemistry={state.chemistry}
              chemStyle={state.chemStyle}
              playerHeight={player?.height}
              gender={player?.gender}
              handleAction={handleAction}
              chemStylesOpen={chemStylesOpen}
              handleChemStylesOpen={handleChemStylesOpen}
            />
          </>
        )}
        <PlayerBio
          displayName={player?.name}
          countryId={player?.countryId}
          clubId={player?.clubId}
          leagueId={player?.leagueId}
          possiblePositions={player?.possiblePositions}
          cardType={player?.cardType}
          versionId={player?.versionId}
          bodyTypeId={player?.bodyTypeId}
          skillMoves={player?.skillMoves}
          weakFoot={player?.weakFoot}
          preferredFoot={player?.preferredFoot}
          weight={player?.weight}
          height={player?.height}
        />
        <DetailedPlayStylesSection
          playStylesPlus={player?.playStylesPlus}
          playStyles={player?.playStyles}
          isGk={player?.preferredPosition === "GK"}
        />
      </div>
    </main>
  );
};
